import { Box, Container, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import CallToAction from '../CallToAction';
import { displayTitleSx } from '../../styles/typography';

const StyledSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  backgroundColor: '#ffffff',
}));

const ImageBox = styled(Paper)(({ theme }) => ({
  position: 'relative',
  borderRadius: '16px',
  overflow: 'hidden',
  height: '100%',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const ProcessStep = ({ image, title, description }: { image: string; title: string; description: string }) => (
  <Box 
    sx={{ 
      width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(33.333% - 16px)' },
      mb: 4 
    }}
  >
    <ImageBox elevation={2}>
      <Box sx={{ position: 'relative' }}>
        <img
          src={image}
          alt={title}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          p: 2,
        }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 0 }}>
            {title}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ p: 3 }}>
        <Typography>{description}</Typography>
      </Box>
    </ImageBox>
  </Box>
);

const Builders = () => {
  const processSteps = [
    {
      image: '/img/builders-panel-01.jpg',
      title: 'Project Partnership',
      description: 'During this phase we engage with you and your plans, the project scope and time frame expectations'
    },
    {
      image: '/img/builders-panel-02.jpg',
      title: 'Account Manager',
      description: 'We assign a specialised Account Manager to liaise with you and your team to ensure a smooth project'
    },
    {
      image: '/img/builders-panel-03.jpg',
      title: 'Design Created',
      description: 'Our experts take your concept and transform this into a bespoke design plan layout'
    },
    {
      image: '/img/builders-panel-04.jpg',
      title: 'Job Call-Up',
      description: "With a large team of professional installers, Hallidays is renowned for our flexibility and availability"
    },
    {
      image: '/img/builders-panel-05.jpg',
      title: 'Rough-In / Fit-Off',
      description: 'We pride ourselves on our quality install, meeting specifications and expectations.'
    },
    {
      image: '/img/builders-panel-06.jpg',
      title: 'Completion',
      description: "The Hallidays brand has been built on quality installation and after-sales care. Our reputation depends upon it."
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#f8fbff',
        py: { xs: 6, md: 10 },
        position: 'relative'
      }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 4, md: 6 }
            }}
          >
            {/* Left Content */}
            <Box 
              sx={{ 
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' }
              }}
            >
              <Typography variant="h1" 
                sx={{
                  ...displayTitleSx,
                  fontWeight: 700,
                  mb: { xs: 2, md: 3 },
                  color: '#0066b2'
                }}
              >
                Builders
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  color: '#445668',
                  lineHeight: 1.6
                }}
              >
                As the leading supplier and installer of quality heating and cooling for the home, 
                we are proud of our reputation of partnering with builders of Victoria to deliver comfort for their designs.
              </Typography>
              <Typography paragraph sx={{ color: '#445668', mb: 2 }}>
                Since 1969 we have partnered with builders and architects of all sizes to deliver a professional, 
                fast, quality service from concept to completion.
              </Typography>
              <Typography paragraph sx={{ color: '#445668', mb: 2 }}>
                Each project is custom designed and individually project managed by our experienced consultants 
                to deliver the best outcome for the homeowner.
              </Typography>
              <Typography paragraph sx={{ color: '#445668' }}>
                Our installers are experienced professionals that care about quality.
              </Typography>
            </Box>
            
            {/* Right Image */}
            <Box 
              sx={{ 
                flex: '1 1 0',
                width: { xs: '100%', sm: '50%' }
              }}
            >
              <Box sx={{
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: { xs: -10, md: -20 },
                  left: { xs: -10, md: -20 },
                  width: '100%',
                  height: '100%',
                  border: '2px solid rgba(0, 102, 178, 0.1)',
                  borderRadius: '16px',
                  zIndex: 0
                }
              }}>
                <img
                  src="/img/img-builders.jpg"
                  alt="Builders"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '16px',
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: '0 8px 24px rgba(0, 102, 178, 0.08)',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Process Section */}
      <StyledSection>
        <Container maxWidth="xl">
          <Typography 
            variant="h2" 
            align="center" 
            sx={{
              ...displayTitleSx,
              mb: { xs: 1, sm: 2 },
              color: '#0066b2',
              fontWeight: 600
            }}
          >
            From Concept to Completion
          </Typography>
          <Typography 
            variant="h5" 
            align="center" 
            sx={{ 
              mb: { xs: 4, sm: 6 },
              color: '#445668',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            How we work with you, <span style={{ color: '#0066b2' }}>"The Hallidays Way"</span>
          </Typography>
          
          {/* Replace Grid with flexbox layout */}
          <Box sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            justifyContent: 'center'
          }}>
            {processSteps.map((step, index) => (
              <ProcessStep key={index} {...step} />
            ))}
          </Box>
        </Container>
      </StyledSection>

      {/* Call to Action */}
      <CallToAction title="Happy Days at Hallidays" />
    </Box>
  );
};

export default Builders; 